import SideMenuButton from "@navigation/SideMenuButton";
import MenuButton from "./MenuButton";
import MenuInception from "./MenuInception";
import { Project, ProjectDocument, ProjectHistoryEvent, ProjectPhoto } from "types";
import { useState, useEffect } from "react";
import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import { ActionsMenuButton } from "./ActionsMenuButton";
import { ModifyProjectModal } from "@components/modals/ModifyProjectModal";
import { DropFileModal, FolderOption, ControlPointOption } from "@components/modals/DropFileModal";
import { EmailModal } from "@components/modals/EmailModal";
import { CompteRenduModal } from "@components/modals/CompteRenduModal";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { projectsApi } from "@services/api";
import { usersApi } from "@services/api/users";
import { invitationsApi } from "@services/api/invitations";
import { Timestamp } from "firebase/firestore";
import { showNotification } from "context/notification.service";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { navigateTo } from "context/navigation.service";

const buildFolderOptions = (project: Project): FolderOption[] => {
  const options: FolderOption[] = [];
  for (const folder of project.folders) {
    options.push({
      value: folder.id,
      label: folder.name,
    });
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        options.push({
          value: `${folder.id}/${sub.id}`,
          label: `${folder.name} / ${sub.name}`,
        });
      }
    }
  }
  return options;
};

const buildControlPointOptions = (project: Project): ControlPointOption[] => {
  const options: ControlPointOption[] = [];
  for (const theme of project.themes) {
    for (const step of theme.steps) {
      for (const cp of step.controlpoints) {
        options.push({
          value: cp.id,
          label: `${step.label} - ${cp.label}`,
        });
      }
    }
  }
  return options;
};

export const ActionsMenuButtonProject = ({
  project,
  setProject,
  isGuest = false,
}: {
  project: Project;
  setProject: (project: Project) => void;
  isGuest?: boolean;
}) => {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const { sentEmails, setSentEmails, removeProjects } = useProjectStore();
  const [modalsOpen, setModalsOpen] = useState<{
    modify: boolean;
    delete: boolean;
    addDocument: boolean;
    addPhoto: boolean;
    compteRendu: boolean;
    changeStatus: boolean;
    sendEmail: boolean;
  }>({
    modify: false,
    delete: false,
    addDocument: false,
    addPhoto: false,
    compteRendu: false,
    changeStatus: false,
    sendEmail: false,
  });

  // Fetch contact metadata for CLIENT section
  const [contactsMetadata, setContactsMetadata] = useState<
    Map<string, { status: string; has_third_party: boolean }>
  >(new Map());

  useEffect(() => {
    if (isGuest) return;
    const contactsWithUser = project.infos.client.contacts.filter(
      (c) => c.user_id,
    );
    if (contactsWithUser.length === 0) return;
    contactsWithUser.forEach(async (contact) => {
      try {
        const user = await usersApi.get(contact.user_id!);
        setContactsMetadata((prev) => {
          const next = new Map(prev);
          next.set(contact.user_id!, {
            status: user.status || "active",
            has_third_party: !!(user.infos.third_party_providers?.length || user.infos.third_party),
          });
          return next;
        });
      } catch {
        // ignore
      }
    });
  }, [project.infos.client.contacts, isGuest]);

  const handleResendAccess = async (contact: {
    user_id: string;
    first_name: string;
    last_name: string;
  }) => {
    try {
      const meta = contactsMetadata.get(contact.user_id);
      if (meta?.status === "pending") {
        await invitationsApi.resend(contact.user_id);
      } else {
        await projectsApi.sendAccessEmail(project.id, contact.user_id);
      }
      showNotification({
        type: "success",
        message: `Email envoyé à ${contact.first_name} ${contact.last_name}`,
      });
    } catch (error) {
      console.error("Error resending access:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'email",
      });
    }
  };

  const handleResetPassword = async (contact: {
    user_id: string;
    first_name: string;
    last_name: string;
  }) => {
    try {
      await usersApi.sendPasswordReset(contact.user_id);
      showNotification({
        type: "success",
        message: `Email de réinitialisation envoyé à ${contact.first_name} ${contact.last_name}`,
      });
    } catch (error) {
      console.error("Error sending password reset:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'email de réinitialisation",
      });
    }
  };

  const handleAddDocuments = async (data: {
    files: File[];
    repertory: string;
    electronicSignature: boolean;
    internalDocument: boolean;
  }) => {
    setModalsOpen({ ...modalsOpen, addDocument: false });

    try {
      const now = Timestamp.now();

      // Create temporary document entries (will be replaced by backend response)
      const tempDocuments: ProjectDocument[] = data.files.map((file) => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        name: file.name,
        file: {
          id: '',
          url: '',
          name: file.name,
          created_at: now,
          octets: file.size,
        },
        hidden: data.internalDocument,
      }));

      // repertory value is either "folderId" or "folderId/subfolderId"
      const parts = data.repertory.split("/");
      const folderId = parts[0];
      const subfolderId = parts[1];

      let updatedFolders = project.folders.map((folder) => {
        if (folder.id !== folderId) return folder;

        if (subfolderId && folder.sub_folders) {
          return {
            ...folder,
            sub_folders: folder.sub_folders.map((sub) =>
              sub.id === subfolderId
                ? {
                    ...sub,
                    documents: [...(sub.documents || []), ...tempDocuments],
                  }
                : sub,
            ),
          };
        }

        return {
          ...folder,
          documents: [...(folder.documents || []), ...tempDocuments],
        };
      });

      const updatedProject = await projectsApi.addDocuments(
        project.id,
        data.files,
        folderId,
        subfolderId,
        data.internalDocument,
      );

      setProject(updatedProject);

      showNotification({
        type: "success",
        message: `${tempDocuments.length} document${tempDocuments.length > 1 ? "s" : ""} ajouté${tempDocuments.length > 1 ? "s" : ""} avec succès`,
      });
    } catch (error) {
      console.error("Error uploading documents:", error);
      showNotification({
        type: "error",
        message: "Erreur lors du téléchargement des documents",
      });
    }
  };

  const handleAddPhotos = async (data: {
    files: File[];
    repertory: string;
    electronicSignature: boolean;
    internalDocument: boolean;
    controlpointId?: string;
  }) => {
    setModalsOpen({ ...modalsOpen, addPhoto: false });

    try {
      const now = Timestamp.now();

      // Create temporary photo entries (will be replaced by backend response)
      const tempPhotos: ProjectPhoto[] = data.files.map((file) => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        file: {
          id: '',
          url: '',
          name: file.name,
          created_at: now,
          octets: file.size,
        },
        hidden: data.internalDocument,
      }));

      const tempPhotoIds = tempPhotos.map((p) => p.id);

      // If a controlpoint is selected, link photos to it
      let updatedThemes = project.themes;
      if (data.controlpointId) {
        updatedThemes = project.themes.map((theme) => ({
          ...theme,
          steps: theme.steps.map((step) => ({
            ...step,
            controlpoints: step.controlpoints.map((cp) =>
              cp.id === data.controlpointId
                ? {
                    ...cp,
                    photos_id: [...(cp.photos_id || []), ...tempPhotoIds],
                  }
                : cp,
            ),
          })),
        }));
      }

      const { compressImages } = await import("@utils/compressImage");
      const compressedFiles = await compressImages(data.files);

      const updatedProject = await projectsApi.addPhotos(
        project.id,
        compressedFiles,
        data.controlpointId,
        data.internalDocument,
      );

      setProject(updatedProject);

      showNotification({
        type: "success",
        message: `${tempPhotos.length} photo${tempPhotos.length > 1 ? "s" : ""} ajoutée${tempPhotos.length > 1 ? "s" : ""} avec succès`,
      });
    } catch (error) {
      console.error("Error uploading photos:", error);
      showNotification({
        type: "error",
        message: "Erreur lors du téléchargement des photos",
      });
    }
  };

  return (
    <>
      {!isGuest && modalsOpen.modify && (
        <ModifyProjectModal
          project={project}
          setProject={setProject}
          onClose={() => setModalsOpen({ ...modalsOpen, modify: false })}
        ></ModifyProjectModal>
      )}
      {!isGuest && modalsOpen.delete && (
        <ConfirmationModal
          title={`Supprimer le projet "${project.infos.general.name}" ?`}
          message="Cette action est irréversible. Tous les documents, photos et données associés seront supprimés."
          confirmText="CONFIRMER"
          onClose={() => setModalsOpen({ ...modalsOpen, delete: false })}
          onConfirm={() => {
            removeProjects([project.id]);
            showNotification({
              type: "success",
              message: "Projet supprimé avec succès",
            });
            setModalsOpen({ ...modalsOpen, delete: false });
            navigateTo({ route: "/projects" });
          }}
        />
      )}
      {modalsOpen.addDocument && (
        <DropFileModal
          onClose={() => setModalsOpen({ ...modalsOpen, addDocument: false })}
          onSave={handleAddDocuments}
          folderOptions={buildFolderOptions(project)}
        ></DropFileModal>
      )}
      {modalsOpen.addPhoto && (
        <DropFileModal
          title="Ajouter des photos"
          iconName="image"
          onClose={() => setModalsOpen({ ...modalsOpen, addPhoto: false })}
          onSave={handleAddPhotos}
          saveButtonLabel="Ajouter"
          showRepertorySelector={false}
          showElectronicSignature={false}
          showImagePreview={true}
          acceptedFileTypes="image/*"
          controlpointOptions={buildControlPointOptions(project)}
        />
      )}
      {!isGuest && modalsOpen.sendEmail && (
        <EmailModal
          onClose={() => setModalsOpen({ ...modalsOpen, sendEmail: false })}
          activeProject={project}
          setActiveProject={setProject}
          userData={userData || undefined}
          orgData={orgData || undefined}
          sentEmails={sentEmails}
          setSentEmails={setSentEmails}
        />
      )}
      {!isGuest && modalsOpen.compteRendu && (
        <CompteRenduModal
          onClose={() => setModalsOpen({ ...modalsOpen, compteRendu: false })}
          project={project}
          setProject={setProject}
          orgId={orgData?.id || ""}
        />
      )}
      <ActionsMenuButton
        project={project}
        setProject={setProject}
        modalsOpen={modalsOpen}
        setModalsOpen={setModalsOpen}
        userData={userData}
        isGuest={isGuest}
        contactsMetadata={contactsMetadata}
        onResendAccess={handleResendAccess}
        onResetPassword={handleResetPassword}
      ></ActionsMenuButton>
    </>
  );
};
